import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import FocusTrap from 'focus-trap-react';

import TranslationProvider, { getTranslation } from '../../../providers/TranslationProvider';
import InfoIcon from '../../../svg/icons/ic_info-outline_red_35x35.svg';
import FavoriteHeart from '../../../svg/icons/ui-favorite.svg';
import Button from '../../Button';
import ClampLines from '../../ClampLines';
import Icon from '../../Icon';
import Loader from '../../Loader';
import FavoriteVenueCard from '../../FavoriteVenueCard';
import MultiStepListModal from '../../MultiStepListModal';
import shareSVG from '../../../svg/icons/ic_share_20x20.svg';
import Ellipsis from '../../../svg/icons/more-horizontal.svg';
import Spinner from '../../../svg/icons/processing-spinner.svg';
import Plus from '../../../svg/icons/add.svg';
import Check from '../../../svg/icons/checkmark.svg';
import { getImageUrl, getNumOfVenuesContent } from '../../../utils';
import ProfilePhoto from '../../ProfilePhoto';

import './MultipleListsSharedView.scss';
import handleCreatedByText from '../../../utils/handleCreatedByText';

function MultipleListsSharedView(
  {
    event,
    count,
    createdBy,
    isMobile,
    isError,
    isFollowing,
    isLoading,
    listId,
    name,
    description,
    onFavoriteClick,
    venues,
    onCopyListClick,
    onFollowListClick,
    urlSlug,
  }
) {
  const [state, setState] = useState({
    isShareLoading: false,
    isFollowLoading: false,
  });
  const [openMenu, setOpenMenu] = useState(false);

  const renderCreatedBy = () => {
    const { display, profilePicture } = createdBy;
    return (
      <div className="MultipleListsSharedView__created-by">
        <div className="MultipleListsSharedView__created-by-profile-pic">
          <ProfilePhoto url={profilePicture} size={20} />
        </div>
        <span>{`${handleCreatedByText(display)} • ${getNumOfVenuesContent(count)}`}</span>
      </div>
    );
  };

  const handleRefreshClick = () => {
    window.location.reload();
  };

  const handleCopyListClick = () => {
    onCopyListClick();
    setOpenMenu(false);
  };

  const onShareButtonClick = useCallback(() => {
    const shareableListUrl = `${window.location.href.split('?')[0]}`;
    setState({ ...state, isShareLoading: true });
    const SHOW_COPIED_CONFIRMATION_TEXT_DURATION = 2000;
    navigator.clipboard.writeText(shareableListUrl).then(() => {
      setState({
        ...state,
        showIsCopiedText: true,
      });
      setTimeout(() => {
        setState({
          ...state,
          showIsCopiedText: false,
          isShareLoading: false,
        });
      }, SHOW_COPIED_CONFIRMATION_TEXT_DURATION);
    });
  }, [listId, state]);

  const renderDescription = () => (
    <div className="MultipleListsSharedView__description">
      <ClampLines
        buttons
        ellipsis="..."
        id="description"
        lessText={getTranslation({
          translationKey: 'less',
          fallback: 'Less',
        })}
        lines={3}
        moreText={getTranslation({
          translationKey: 'more',
          fallback: 'More',
        })}
        text={description}
      />
    </div>
  );

  const renderHeader = () => (
    <div className="MultipleListsSharedView__header">
      <div className="MultipleListsSharedView__header-content">
        <h2 className="MultipleListsSharedView__title">
          {name}
        </h2>
        {description && renderDescription()}
        {renderCreatedBy()}
      </div>
      <div className="MultipleListsSharedView__cta-top">
        <div className="MultipleListsSharedView__cta-top-button-wrapper">
          <Button
            className="MultipleListsSharedView__share-menu-button"
            onClick={onShareButtonClick}
            data-test-id="list-share-button"
            sm
          >
            {state.isShareLoading ? (
              <Spinner className="MultipleListsDetailView__loading-icon" height="15px" width="15px" fill="#EAEAEA" />
            ) : (
              <Icon
                className="MultipleListsSharedView__share-icon"
                component={shareSVG}
                name="upload"
                height="15px"
                width="15px"
              />
            )}
            {state.showIsCopiedText ? (
              <TranslationProvider translationKey="button_copied_bang">
                <span aria-live="polite">Copied!</span>
              </TranslationProvider>
            ) : (
              <TranslationProvider translationKey="share">
                <span>Share</span>
              </TranslationProvider>
            )}
          </Button>
          <Button
            aria-expanded={openMenu}
            aria-label={getTranslation({
              translationKey: 'lists.menu',
              fallback: 'Lists Menu',
            })}
            className="MultipleListsSharedView__ellipsis-menu-button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpenMenu(!openMenu);
            }}
            variant="no-style"
            type="button"
          >
            <Icon
              name="ellipsis"
              component={Ellipsis}
              height="20px"
              width="20px"
            />
          </Button>
          <FocusTrap
            active={openMenu}
            focusTrapOptions={{
              allowOutsideClick: false,
              clickOutsideDeactivates: true,
              initialFocus: 'false',
              onDeactivate: () => {
                setTimeout(() => { setOpenMenu(false); }, 200);
              },
            }}
          >
            <div className={cx('MultipleListsSharedView__menu', {
              'MultipleListsSharedView__menu--open': openMenu,
            })}
            >
              <Button
                data-test-id="MultipleListsDetailView__edit-button"
                variant="no-style"
                onClick={handleCopyListClick}
                type="button"
              >
                <Icon
                  name="add"
                  component={Plus}
                  height="20px"
                  width="20px"
                />
                <TranslationProvider
                  translationKey="lists.copy"
                >
                  <div className="MultipleListsDetailView__edit-button-text">
                    Copy to My Lists
                  </div>
                </TranslationProvider>
              </Button>
            </div>
          </FocusTrap>
        </div>
      </div>
    </div>
  );

  const renderNoVenues = () => (
    <div className="MultipleListsSharedView__error">
      <div className="MultipleListsSharedView__error-icon">
        <Icon name="favorite-heart" component={FavoriteHeart} width="45px" height="45px" />
      </div>
      <h3 className="MultipleListsSharedView__error-title">
        {getTranslation({
          translationKey: 'errors.lists.no_data_title',
          fallback: 'No Venues Added to List',
        })}
      </h3>
    </div>
  );

  const onFollowButtonClick = async (follow) => {
    setState({ ...state, isFollowLoading: true });
    await onFollowListClick(follow);
    setState({ ...state, isFollowLoading: false });
  };

  const renderFollowing = () => (
    <div className="MultipleListsSharedView__follow-button-container">
      {isFollowing
        && (
        <Button
          className="MultipleListsSharedView__follow-button"
          onClick={() => {
            onFollowButtonClick(false);
          }}
          variant="secondary"
          data-testid="MultipleListsSharedView-follow-button"
        >
          {state.isFollowLoading ? (
            <Spinner className="MultipleListsDetailView__loading-icon" height="15px" width="15px" fill="#EAEAEA" />
          ) : (
            <Icon name="check" component={Check} width="15" height="15px" />
          )}
          <span>
            {getTranslation({
              translationKey: 'following_list',
              fallback: 'Following',
            })}
          </span>
        </Button>
        )}
      {!isFollowing
        && (
        <Button
          className="MultipleListsSharedView__follow-button"
          onClick={() => {
            onFollowButtonClick(true);
          }}
          data-testid="MultipleListsSharedView-no-follow-button"
        >
          {state.isFollowLoading ? (
            <Spinner className="MultipleListsDetailView__loading-icon" height="15px" width="15px" fill="#EAEAEA" />
          ) : (
            <Icon name="check" component={Check} width="15" height="15px" />
          )}
          <span>
            {getTranslation({
              translationKey: 'follow_list',
              fallback: 'Follow List',
            })}
          </span>
        </Button>
        )}
    </div>
  );

  if (isLoading) return <Loader />;

  if (isError) {
    return (
      <div className="MultipleListsSharedView__error">
        <div className="MultipleListsSharedView__error-icon">
          <Icon component={InfoIcon} width="35px" height="35px" />
        </div>
        <h3 className="MultipleListsSharedView__error-title">
          {getTranslation({
            translationKey: 'errors.lists.no_data_title',
            fallback: 'Oops! Unable to load page.',
          })}
        </h3>
        <p className="MultipleListsSharedView__error-body">
          {getTranslation({
            translationKey: 'errors.lists.no_data_body',
            fallback: 'Please try again.',
          })}
        </p>
        <Button
          sm
          className="MultipleListsSharedView__error-cta"
          onClick={handleRefreshClick}
        >
          {getTranslation({
            translationKey: 'errors.lists.no_data_cta',
            fallback: 'Try Again',
          })}
        </Button>
      </div>
    );
  }

  const hasVenues = (venues.length > 0);

  return (
    <div className="MultipleListsSharedView">
      {renderHeader()}
      {renderFollowing()}
      {!hasVenues && renderNoVenues()}
      {hasVenues && (
        venues.map((restaurant) => (
          <FavoriteVenueCard
            key={restaurant.id}
            venue={{
              ...restaurant,
              collections: [],
              image: getImageUrl(restaurant.responsiveImages),
              slots: [],
            }}
            onFavoriteClick={(favorite) => onFavoriteClick(favorite)}
            isMobile={isMobile}
            event={event}
          />
        )))}
      <MultiStepListModal
        Analytics={{ event }}
        description={description}
        name={name}
        listId={listId}
        urlSlug={urlSlug}
      />
    </div>
  );
}

MultipleListsSharedView.propTypes = {
  event: PropTypes.func.isRequired,
  count: PropTypes.number,
  createdBy: PropTypes.shape({
    display: PropTypes.string,
    profilePicture: PropTypes.string,
  }),
  description: PropTypes.string,
  isError: PropTypes.bool,
  isFollowing: PropTypes.bool,
  isLoading: PropTypes.bool,
  isMobile: PropTypes.bool,
  listId: PropTypes.string,
  name: PropTypes.string,
  urlSlug: PropTypes.string,
  onFavoriteClick: PropTypes.func.isRequired,
  onFollowListClick: PropTypes.func.isRequired,
  onCopyListClick: PropTypes.func.isRequired,
  venues: PropTypes.shape([]),
};

MultipleListsSharedView.defaultProps = {
  count: 0,
  createdBy: undefined,
  description: undefined,
  isError: false,
  isFollowing: false,
  isLoading: false,
  isMobile: false,
  listId: undefined,
  name: '',
  venues: [],
  urlSlug: undefined,
};

export default MultipleListsSharedView;
